import React, { useMemo, useState } from 'react';
import * as turf from '@turf/turf';
import { isEqual } from 'lodash';

import { useDispatch, useSelector } from 'react-redux';
import { cancelSearchAsync, startSearchAsync, exportSearchAsync, clearSearch } from '../../../../../redux/search/Actions';

import { makeStyles, Button, Typography, Grid, MenuItem } from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';

import Progress from '../../../../Common/UI/Progress';
import RunAnalysisDialog from '../../../../Common/Analysis/RunAnalysisDialog';
import MoreOptionsButton from '../../common/MoreOptionsButton';
import CriteriaValidator from './CriteriaValidator';
import ConfirmCriteriaDialog from './ConfirmCriteriaDialog';

const useStyles = makeStyles((theme) => styles(theme));

function SearchActions ({ onScrollToCriteria }) {
    const classes = useStyles();
    const currentProject = useSelector(state => state.project.currentProject);
    const error = useSelector(state => state.search.error);
    const source = useSelector(state => state.search.searchArea.layerId);
    const layer = useSelector(state => state.layers.layers.find(l => l.id === source));
    const searchLayers = useSelector(state => state.layers.searchLayers);
    const areaFacet = useSelector(state => state.lookups.uiData.facetChoices.find(el => el.id === state.search.searchArea.facetId));
    const areaFacetValue = useSelector(state => state.search.searchArea.facetValue);
    const areaValid = useSelector(state => state.search.searchArea.facetValid);
    const geojson = useSelector(state => state.search.searchArea.geojson);
    const criteria = useSelector(state => state.search.searchArea.criteria);
    const isSearching = useSelector(state => state.search.isSearching);
    const isAnalysing = useSelector(state => state.search.isAnalysing);
    const search = useSelector(state => state.search.search);
    const isSearchDirty = useSelector(state => state.search.isSearchDirty);
    const searchFeatures = useSelector(state => state.user.features['siteFinder']);
    const searchLayerFeature = searchFeatures.layers.find(l => l.layerId === source);
    const { searchExportEnabled, analysisEnabled, searchRunLabel } = searchLayerFeature;
    const maxArea = searchFeatures.maxArea;
    const maxAnalysis = searchFeatures.maxAnalysis;

    const dispatch = useDispatch();
    function dispatchStartSearchAsync (layer, areaFacetId, areaFacetValue, geom, criteria) {
        dispatch(startSearchAsync(currentProject.id, layer, areaFacetId, areaFacetValue, geom, criteria));
    }
    function dispatchCancelSearchAsync (id) {
        dispatch(cancelSearchAsync(id));
    }
    function dispatchExportSearchAsync (id) {
        dispatch(exportSearchAsync(id));
    }
    function dispatchClearSearch () {
        dispatch(clearSearch());
    }

    const [isRunAnalysisDialogOpen, setIsRunAnalysisDialogOpen] = useState(false);
    const [isConfirmCriteriaDialogOpen, setIsConfirmCriteriaDialogOpen] = useState(false);
    // Need this to auto back to false when search changes

    function validateSearch () {
        // const result = checkValidity(criteria);

        // if(!result){
        //     return false;
        // }
        if(!areaFacet) {
            return false;
        }

        const criteriaErrors = CriteriaValidator(criteria, layer?.filterConfig?.fields, searchLayers);
        if (!Boolean(Object.keys(criteriaErrors).length === 0)) {
            return false;
        }
        // console.log('isvalid',Boolean(Object.keys(criteriaErrors).length === 0), criteriaErrors);

        switch (areaFacet.layerType) {
            case 'all':
                return true;
            case 'drawn':
                if (!geojson) { return false; }
                if (turf.area(geojson.geometry) > maxArea) { return false; }
                return true;
            case 'fixed':
                if (!geojson) { return false; }
                // if (turf.area(geojson.geometry) > maxArea) { return false; }
                return areaValid;
            default:
                console.error(`Unimplemented Area Type ${areaFacet.layerType}`);
                return false;
        }
    }

    function validateAnalysis () {
        return search &&
            search.computedData &&
            search.computedData.length > 0 &&
            search.computedData.length < maxAnalysis;
    }

    const isValidSearch = validateSearch();
    const isValidAnalysis = validateAnalysis();

    function getDescription (total) {
        if (total > maxAnalysis) { 
            return `Too many results have been returned to run an analysis. Please refine your search by specifying a different area, or adjusting your criteria so there is less than ${maxAnalysis} results`; 
        } else if (total === 0) { 
            return 'No results where found. Please refine your search by specifying a different area, or adjusting your criteria'; 
        } else { 
            return 'You can refine your search by specifying a different area, or adjusting your criteria'; 
        }
    }

    const blankTree = {
        id: 'root',
        type: 'group',
        conjunction: 'and',
        not: false,
        children: []
    };

    function runSearch () {
        if ((!isConfirmCriteriaDialogOpen) && isEqual(criteria, layer.filterConfig.defaultTree || blankTree)) {
            setIsConfirmCriteriaDialogOpen(true);
            return;
        }
        setIsConfirmCriteriaDialogOpen(false);
        if (validateSearch()) { 
            dispatchStartSearchAsync(source, areaFacet.id, areaFacetValue, geojson?.geometry, criteria); 
        }
    }

    function cancelSearch () {
        if (search) { 
            dispatchCancelSearchAsync(search.id); 
        }
    }

    function handleClear () {
        if (!isSearching) {
            dispatchClearSearch();
        }
    }

    function openRunAnalysisDialog () {
        if (validateAnalysis()) { 
            setIsRunAnalysisDialogOpen(true);
        }
    }

    function closeRunAnalysisDialog () {
        setIsRunAnalysisDialogOpen(false);
    }

    function closeConfirmCriteriaDialog () {
        onScrollToCriteria();
        setIsConfirmCriteriaDialogOpen(false);
    }

    function checkValidity(data) {
        function checkGroup(group) {
            if(group === null)
                return true;
            for (const child of group.children) {
                if (child.children && child.children.length > 0) {
                    if (!checkGroup(child)) {
                        return false;
                    }
                } 
                if (child.isValid === false && child?.type === "group-rule") {
                    return true;
                } else if (child.isValid === false) {
                    return false;
                }
            }
            return true;
        }
        
        return checkGroup(data);
    }

    function handleExportClick () {
        if (search) { dispatchExportSearchAsync(search.id); }
    }

    function SearchStart() {
        const isMoreOptions = (search?.computedData && search?.computedData.length > 0) && (searchExportEnabled);
        return (
            <Grid container spacing={2}>
                <Grid item xs={4}>
                    <Button
                        variant="outlined"
                        size="large"
                        color="secondary"
                        fullWidth
                        onClick={isSearching ? cancelSearch : handleClear}
                    >
                        { isSearching ? 'Cancel' : 'Clear' }
                    </Button>
                </Grid>
                <Grid item xs={isMoreOptions ? 7 : 8}>
                    <Button
                        variant="contained"
                        size="large"
                        color="secondary"
                        fullWidth
                        disabled={isSearching || !isSearchDirty || !isValidSearch}
                        onClick={runSearch}
                    >
                        { isSearching ? 'Searching' : (searchRunLabel || 'Find Land') }
                    </Button>
                </Grid>
                {
                    isMoreOptions &&
                    <Grid item xs={1}>
                        <MoreOptionsButton>
                            {
                                searchExportEnabled &&
                                <MenuItem
                                    onClick={handleExportClick}
                                    disabled={!search || search?.computedData.length === 0}
                                >
                                    Export as CSV
                                </MenuItem>
                            }
                        </MoreOptionsButton>
                    </Grid>
                }
            </Grid>
        );
    }

    function SearchComplete() {
        const isMoreOptions = searchExportEnabled;
        return (
            <Grid container spacing={2}>
                <Grid item xs={4}>
                    <Button
                        variant="outlined"
                        size="large"
                        color="secondary"
                        fullWidth
                        onClick={handleClear}
                    >
                        Clear
                    </Button>
                </Grid>
                <Grid item xs={isMoreOptions ? 7 : 8}>
                    <Button
                        variant="contained"
                        size="large"
                        color="secondary"
                        fullWidth
                        disabled={!analysisEnabled || !isValidAnalysis}
                        onClick={openRunAnalysisDialog}
                    >
                        Run Analysis
                    </Button>
                </Grid>
                {
                    isMoreOptions &&
                    <Grid item xs={1}>
                        <MoreOptionsButton>
                            {
                                searchExportEnabled &&
                                <MenuItem
                                    onClick={handleExportClick}
                                >
                                    Export as CSV
                                </MenuItem>
                            }
                        </MoreOptionsButton>
                    </Grid>
                }
            </Grid>
        );
    }

    return (
        <>
            {
                Boolean(search && search.status === 'complete') &&
                <Alert icon={false} severity={isValidAnalysis ? 'success' : 'error'} className={classes.alertRoot}>
                    <AlertTitle>
                        Potential results: {search.computedData.length}
                    </AlertTitle>
                    {getDescription(search.computedData.length)}
                </Alert>
            }
            {
                Boolean(error) &&
                <Alert icon={false} severity="error" className={classes.alertRoot}>
                    <Typography variant="subtitle1">
                        {error.status} {error.statusText}
                    </Typography>
                    <Typography variant="body2">
                        {error.data}
                    </Typography>
                </Alert>
            }
            {
                Boolean(search && ['running', 'queued', 'ready'].includes(search.status)) && 
                <Progress
                    progress={search.progress || 0}
                    status={search.status}
                    remainingTime={search.remainingTime}
                />
            }
            <div className={classes.actionsRoot}>
                {
                    (!search || isSearchDirty || isSearching) ?
                        SearchStart() :  
                        SearchComplete()
                }
            </div>
            <RunAnalysisDialog 
                open={isRunAnalysisDialogOpen}
                onClose={closeRunAnalysisDialog}
            />
            <ConfirmCriteriaDialog
                open={isConfirmCriteriaDialogOpen}
                onClose={closeConfirmCriteriaDialog}
                onConfirm={runSearch}
            />
        </>
    );
}

const styles = theme => ({
    actionsRoot: {
        display: 'flex',
        '& > *': {
            flex: '1 1 0',
            '&:not(:last-child)': {
                marginRight: theme.constants.spacing.xl
            }
        }
    },
    alertRoot: {
        padding: '10px'
    }
});

export default SearchActions;